import { Injectable, inject } from '@angular/core';
import { CdsPopupService, MatDialogRef } from '@cds/ng-web-components/popup';
import { UserAgreementPopupComponent } from 'src/app/shared/user-agreement-popup/user-agreement-popup.component';
import { Session } from 'src/app/core/models/user';
import { getSessionByKey } from 'src/app/utils/indexdb';

@Injectable({ providedIn: 'root' })
export class SessionConflictService {
  private cdsPopup = inject(CdsPopupService);

  private readonly sessionConflictWindowMs = 30 * 60 * 1000;
  private sessionInvalidated = false;

  async hasConflict(incomingUserId: string): Promise<boolean> {
    const existingSession = await this.getStoredSession();
    if (!existingSession.id || this.isSessionExpired(existingSession.currentLastUpdate)) {
      return false;
    }

    return this.normalizeUserId(existingSession.id) !== this.normalizeUserId(incomingUserId);
  }

  private getStoredSession(): Promise<Session> {
    return getSessionByKey();
  }

  private isSessionExpired(currentLastUpdate: number): boolean {
    return !Number.isFinite(currentLastUpdate) || Date.now() - currentLastUpdate > this.sessionConflictWindowMs;
  }

  private normalizeUserId(userId: string): string {
    return (userId || '').trim().toLowerCase();
  }

  showConflictDialog(): Promise<void> {
    return new Promise(resolve => {
      const popupRef: MatDialogRef<UserAgreementPopupComponent> = this.cdsPopup.open(UserAgreementPopupComponent, {
        size: 'sm',
        data: {
          message: 'login.accountConflict.message',
          continue: 'common.ok',
          type: 'alert',
        },
      });
      popupRef.afterClosed().subscribe(() => resolve());
    });
  }

  broadcastSessionChange(userId: string): void {
    localStorage.setItem('sessionUserId', userId);
    localStorage.setItem('sessionChangeTimestamp', String(Date.now()));
  }

  startListening(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'sessionChangeTimestamp' && event.newValue) {
        this.sessionInvalidated = true;
      }
    });

    const interactionHandler = () => {
      if (this.sessionInvalidated) {
        window.location.reload();
      }
    };
    window.addEventListener('click', interactionHandler, true);
    window.addEventListener('keydown', interactionHandler, true);
  }
}
